import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useT } from '../lib/i18n';
import { cn } from '../lib/cn';
import { useAuth } from '../contexts/AuthContext';
import {
  IconBrain, IconCompose, IconLightbulb, IconClock, IconDocument,
  IconChartBar, IconSparkles, IconBookOpen, IconSearch, IconChatBubble,
  IconChartPie, IconCog,
} from './icons/SidebarIcons';

interface MenuItem {
  id: string;
  icon: React.FC<{ className?: string }>;
  labelKey: string;
  path: string;
}

interface MenuGroup {
  titleKey: string;
  items: MenuItem[];
}

const menuGroups: MenuGroup[] = [
  {
    titleKey: 'sidebar.sections.create',
    items: [
      { id: 'brain-dump', icon: IconBrain, labelKey: 'sidebar.nav.brainDump', path: '/brain-dump' },
      { id: 'compose', icon: IconCompose, labelKey: 'sidebar.nav.composeIdeas', path: '/compose' },
      { id: 'coach', icon: IconLightbulb, labelKey: 'sidebar.nav.contentCoach', path: '/coach' },
      { id: 'reply', icon: IconChatBubble, labelKey: 'sidebar.nav.replyGuy', path: '/reply' },
    ],
  },
  {
    titleKey: 'sidebar.sections.analyze',
    items: [
      { id: 'analytics', icon: IconChartBar, labelKey: 'sidebar.nav.analytics', path: '/analytics' },
      { id: 'algo', icon: IconChartPie, labelKey: 'sidebar.nav.algoAnalyzer', path: '/algo' },
      { id: 'researcher', icon: IconSearch, labelKey: 'sidebar.nav.accountResearcher', path: '/researcher' },
    ],
  },
  {
    titleKey: 'sidebar.sections.grow',
    items: [
      { id: 'inspiration', icon: IconSparkles, labelKey: 'sidebar.nav.inspiration', path: '/inspiration' },
      { id: 'education', icon: IconBookOpen, labelKey: 'sidebar.nav.education', path: '/education' },
    ],
  },
];

const bottomItems: MenuItem[] = [
  { id: 'history', icon: IconClock, labelKey: 'sidebar.nav.history', path: '/history' },
  { id: 'drafts', icon: IconDocument, labelKey: 'sidebar.nav.drafts', path: '/drafts' },
  { id: 'admin', icon: IconCog, labelKey: 'sidebar.nav.admin', path: '/admin' },
];

export const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, locale, setLocale } = useT();
  const { user, isAuthenticated, signOut } = useAuth();

  const NavButton = ({ item }: { item: MenuItem }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    return (
      <button
        onClick={() => navigate(item.path)}
        className={cn(
          'group/nav w-full py-[7px] px-2.5 border-none cursor-pointer text-left',
          'flex items-center gap-2.5 rounded-lg',
          'transition-all duration-150 ease-out',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50',
          isActive
            ? 'bg-brand-muted text-brand'
            : 'bg-transparent text-text-secondary hover:bg-white/[0.04] hover:text-text-primary'
        )}
      >
        <Icon
          className={cn(
            'w-[18px] h-[18px] flex-shrink-0 transition-colors duration-150',
            isActive ? 'text-brand' : 'text-text-muted group-hover/nav:text-text-secondary'
          )}
        />
        <span className={cn(
          'text-[13px] truncate leading-none',
          isActive ? 'font-semibold' : 'font-medium'
        )}>
          {t(item.labelKey)}
        </span>
      </button>
    );
  };

  return (
    <aside className="w-full h-full bg-surface-1 border-r border-border flex flex-col">
      {/* Logo */}
      <div className="px-4 py-4">
        <div className="flex items-center gap-2.5">
          <img src="/logo192.png" alt="Bangerfy" className="w-8 h-8 rounded-[10px]" />
          <div>
            <h1 className="text-[15px] font-bold m-0 text-text-primary leading-tight tracking-tight">
              Bangerfy
            </h1>
            <p className="text-[10px] text-text-muted m-0 leading-tight font-medium">
              {t('sidebar.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Nav Groups */}
      <nav className="flex-1 min-h-0 overflow-y-auto py-2 px-2.5 space-y-3">
        {menuGroups.map((group) => (
          <div key={group.titleKey}>
            <p className="text-[10px] uppercase tracking-[0.08em] text-text-faint font-semibold px-2.5 mb-1">
              {t(group.titleKey)}
            </p>
            <div className="space-y-px">
              {group.items.map((item) => (
                <NavButton key={item.id} item={item} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Bottom */}
      <div className="border-t border-border-subtle px-2.5 py-2 space-y-px">
        {bottomItems.map((item) => (
          <NavButton key={item.id} item={item} />
        ))}
      </div>

      {/* Auth */}
      <div className="px-2.5 py-2 border-t border-border-subtle">
        {isAuthenticated ? (
          <div className="flex items-center gap-2.5 px-2.5 py-[7px]">
            <div className="w-7 h-7 rounded-full bg-brand/20 flex items-center justify-center flex-shrink-0">
              <span className="text-[11px] font-bold text-brand">
                {(user?.email?.[0] || 'U').toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] text-text-primary font-medium truncate m-0">{user?.email}</p>
            </div>
            <button
              onClick={() => signOut()}
              className="bg-transparent border-none cursor-pointer p-1 text-text-faint hover:text-text-muted transition-colors"
              title="Log out"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
        ) : (
          <button
            onClick={() => navigate('/login')}
            className="w-full py-[7px] px-2.5 border border-border rounded-lg cursor-pointer text-left flex items-center gap-2.5 bg-transparent text-text-secondary hover:bg-white/[0.04] hover:text-text-primary transition-all duration-150"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-muted flex-shrink-0">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
              <polyline points="10 17 15 12 10 7" />
              <line x1="15" y1="12" x2="3" y2="12" />
            </svg>
            <span className="text-[13px] font-medium">Log in</span>
          </button>
        )}
      </div>

      {/* Language Switcher */}
      <div className="px-3 py-2.5 border-t border-border-subtle">
        <div className="flex gap-1 bg-surface-2 rounded-lg p-0.5">
          <button
            onClick={() => setLocale('en')}
            className={cn(
              'flex-1 py-1 rounded-md text-[11px] font-medium transition-all duration-150 border-none cursor-pointer',
              locale === 'en'
                ? 'bg-surface-3 text-text-primary shadow-sm'
                : 'bg-transparent text-text-muted hover:text-text-secondary'
            )}
          >
            EN
          </button>
          <button
            onClick={() => setLocale('ja')}
            className={cn(
              'flex-1 py-1 rounded-md text-[11px] font-medium transition-all duration-150 border-none cursor-pointer',
              locale === 'ja'
                ? 'bg-surface-3 text-text-primary shadow-sm'
                : 'bg-transparent text-text-muted hover:text-text-secondary'
            )}
          >
            JA
          </button>
        </div>
      </div>
    </aside>
  );
};
